import type { Incident } from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';

const GROUPED_EVIDENCE_REVIEW_OUTCOMES = new Set([
  'true_event',
  'insufficient_evidence',
  'duplicate',
]);

const VERSION_CONFLICT_COPY =
  'This case was updated while evidence was still arriving. Close this dialog and confirm again.';

function isVersionConflictError(raw: unknown): boolean {
  const text = String(raw || '').trim();
  return text === 'version_conflict' || /version[_ ]conflict/i.test(text);
}

function humanizeReviewDecisionError(raw: unknown): string {
  if (isVersionConflictError(raw)) return VERSION_CONFLICT_COPY;
  return String(raw || 'Failed to save review decision');
}

export type PersistIncidentReviewDecisionArgs = {
  incidentId: string;
  selectedIncidentId: string;
  selectedIncidentVersion: number;
  groupedReviewMode: boolean;
  reason: string;
  note: string;
  tags: string[];
  reviewOutcome?: string;
  applySelectedIncident: (incident: Incident) => void;
  refreshQueueQuietly: () => void;
  refreshSelectedIncident: () => void | Promise<unknown>;
};

export type PersistIncidentReviewDecisionResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Persist a reviewer confirm/close decision with one optimistic-concurrency retry.
 * Evidence promotion often bumps version while the confirm dialog is open.
 */
export async function persistIncidentReviewDecision(
  args: PersistIncidentReviewDecisionArgs,
): Promise<PersistIncidentReviewDecisionResult> {
  const {
    incidentId,
    selectedIncidentId,
    selectedIncidentVersion,
    groupedReviewMode,
    reason,
    note,
    tags,
    reviewOutcome,
    applySelectedIncident,
    refreshQueueQuietly,
    refreshSelectedIncident,
  } = args;

  let expectedVersion = Number(selectedIncidentVersion || 1);
  if (selectedIncidentId !== incidentId) {
    try {
      const representative = await incidentsApi.getIncident(incidentId);
      expectedVersion = Number((representative as any)?.version || 1);
    } catch {
      // Fall back to the loaded row version if the representative fetch fails.
    }
  }

  const nextStatus = (
    groupedReviewMode
    && reviewOutcome
    && GROUPED_EVIDENCE_REVIEW_OUTCOMES.has(String(reviewOutcome).trim().toLowerCase())
  )
    ? 'IN_REVIEW'
    : 'CLOSED';

  const statusBody = {
    status: nextStatus as any,
    close_reason: reason as any,
    close_note: note || undefined,
    review_outcome: reviewOutcome as any,
    operator_tags: tags,
  };

  const applySuccess = (fresh: Incident) => {
    applySelectedIncident(fresh);
    refreshQueueQuietly();
    void refreshSelectedIncident();
  };

  try {
    const fresh = await incidentsApi.patchStatus(
      incidentId,
      statusBody,
      { expected_version: expectedVersion },
    );
    applySuccess(fresh);
    return { ok: true };
  } catch (error: any) {
    let failure: any = error;
    let recoveredIncident: Incident | null = null;
    try {
      recoveredIncident = await incidentsApi.getIncident(incidentId);
    } catch {
      recoveredIncident = null;
    }

    const recoveredStatus = String((recoveredIncident as any)?.status || '').trim().toUpperCase();
    const recoveredOutcome = String((recoveredIncident as any)?.review_outcome || '').trim().toLowerCase();
    const recoveredVersion = Number((recoveredIncident as any)?.version || 0);
    const outcomeApplied = !reviewOutcome || recoveredOutcome === String(reviewOutcome).trim().toLowerCase();
    const statusApplied = recoveredStatus === String(nextStatus).trim().toUpperCase();
    if (recoveredIncident && recoveredVersion > expectedVersion && outcomeApplied && statusApplied) {
      if (selectedIncidentId === incidentId) {
        applySelectedIncident(recoveredIncident);
      }
      refreshQueueQuietly();
      return { ok: true };
    }

    const apiError = failure?.response?.data?.error || failure?.message;
    if (isVersionConflictError(apiError) && recoveredIncident && recoveredVersion > 0) {
      try {
        const retried = await incidentsApi.patchStatus(
          incidentId,
          statusBody,
          { expected_version: recoveredVersion },
        );
        applySuccess(retried);
        return { ok: true };
      } catch (retryError: any) {
        const retryApiError = retryError?.response?.data?.error || retryError?.message;
        if (isVersionConflictError(retryApiError)) {
          return { ok: false, message: VERSION_CONFLICT_COPY };
        }
        failure = retryError;
      }
    }

    const raw = failure?.response?.data?.detail
      || failure?.response?.data?.error
      || failure?.message
      || 'Failed to save review decision';
    return { ok: false, message: humanizeReviewDecisionError(raw) };
  }
}
